import React from 'react';
import { Button } from '@view/components';
import {IViewSettings, ObjectTypes} from '@data/models';
import styles from './styles.scss';
import { ConfigProvider } from '@data/providers';
import { ISceneData } from '@data/providers/SceneDataProvider';
import {BooleanKeys, ObjectKeys} from '@types';
import { cn } from '@utils';
import { useCompanyColorScheme } from 'src/hooks/useCompanyColorScheme';
import GridSwitcher from './GridSwitcher';
import SemitransparencySwitcher from '@view/layouts/ViewSettings/SemitransparencySwitcher';

export type GridOption = {
  value: number | null;
  label: string;
};

type viewSettingsButton = {
  value: BooleanKeys<IViewSettings> | ObjectKeys<IViewSettings>;
  label: string;
  disabled?: (sceneData?: ISceneData) => boolean;
  disabledHint?: string;
  onChange?: (settingEnabled: boolean, setViewSetting: (setting: keyof IViewSettings, value: any) => void) => void;
  hidden?: (configProvider: ConfigProvider, sceneData?: ISceneData) => boolean;
  active?: (viewSetting: boolean | object | undefined) => boolean;
  customComponent?: React.FC<any>;
  customProps?: object;
};

interface IViewSettingsProps {
  configProvider: ConfigProvider;
  viewSettings: IViewSettings;
  toggleViewSetting: (setting: BooleanKeys<IViewSettings> | ObjectKeys<IViewSettings>) => void;
  className?: string;
  sceneData?: ISceneData;
  setViewSetting: (setting: keyof IViewSettings, value: any) => void;
  companyColorScheme?: ReturnType<typeof useCompanyColorScheme>[0];
}

const settings: viewSettingsButton[] = [
  {
    value: 'teethLikeSemitransparent',
    label: 'Semitransparency',
    customComponent: SemitransparencySwitcher
  },
  {
    value: 'grid',
    label: 'Grid',
    customComponent: GridSwitcher,
    hidden: configProvider => !configProvider.isFeatureEnabled('grid'),
  },
  {
    value: 'overlay',
    label: 'Overlay',
    hidden: configProvider=> !configProvider.isFeatureEnabled("overlay")
  },
  { value: 'attachments', label: 'Attachments' },
  { value: 'translateBlocks', label: 'Translate blocks' },
  { value: 'biteRamps', label: 'Bite ramps' },
  { value: 'elastics', label: 'Elastics' },
  { value: 'pontics', label: 'Pontics' },
  { value: 'iprs', label: 'Separation' },
  {
    value: 'occlusions',
    label: 'Contacts',
    hidden: (_, sceneData) => !sceneData?.occlusions.length
  },
  {
    value: 'hideGum',
    label: 'Gum',
    active: value => !value,
    hidden: configProvider => !configProvider.isFeatureEnabled('hideGum')
  },
  {
    value: 'showCbctJaws',
    label: 'Bones',
    disabled: (sceneData?: ISceneData) => !Object.values(sceneData?.allObjects || {}).some(obj => obj.type === ObjectTypes.CbctJaw),
    disabledHint: "Bones are not available",
    onChange: (showCbctJaws, setViewSetting) => {
      if (showCbctJaws) setViewSetting("hideGum", true);
    }
  },
];

const ViewSettings = ({
  configProvider,
  viewSettings,
  toggleViewSetting,
  className,
  sceneData,
  companyColorScheme,
  setViewSetting,
}: IViewSettingsProps) => {

  return (
    <div className={cn(styles.viewSettingsBar_group, className)}>
      {settings
        .filter(setting => !setting.hidden?.(configProvider, sceneData))
        .map((setting) => {
          if (setting.customComponent) {
            const CustomComponent = setting.customComponent;
            return (
              <CustomComponent
                key={setting.value}
                viewSettings={viewSettings}
                setViewSetting={setViewSetting}
                companyColorScheme={companyColorScheme}
                configProvider={configProvider}
                {...(setting.customProps || {})}
                sceneData={sceneData}
              />
            );
          }
          const isDisabled = setting.disabled?.(sceneData);

          return (
            <Button
              key={setting.value}
              tooltip={isDisabled ? setting.disabledHint : setting.label}
              active={setting.active ? setting.active(viewSettings?.[setting.value]) : viewSettings?.[setting.value]}
              className={isDisabled ? cn(styles.viewSettingsBar_button__disabled) : cn(styles.viewSettingsBar_button)}
              onClick={() => {
                if (isDisabled) return;
                if (setting.onChange) {
                  const newViewSetting: boolean = !viewSettings?.[setting.value];
                  setting.onChange(newViewSetting, setViewSetting);
                }
                toggleViewSetting(setting.value);
              }}
            >
              <img
                src={`/assets/images/${setting.value}_icon.svg`}
                className={cn(
                  companyColorScheme?.light && 'invert-[.75] drop-shadow-lg'
                )}
                alt="View Settings"
              />
            </Button>
          );
        })}
    </div>
  );
};

export default ViewSettings;
